import { useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const KEY = 'smi_gate_ok';

export const SiteGate = ({ children }) => {
    const [state, setState] = useState(() => {
        try { if (localStorage.getItem(KEY) === '1') return 'open'; } catch { /* ignore */ }
        // Prerendered pages carry the gate state captured at build time (data-gate on #root):
        // render content immediately, confirm with the API in the background (effect below).
        try { if (document.getElementById('root')?.getAttribute('data-gate') === 'open') return 'open-unconfirmed'; } catch { /* ignore */ }
        return 'checking';
    });
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (state !== 'checking' && state !== 'open-unconfirmed') return;
        const optimistic = state === 'open-unconfirmed';
        fetch(`${API}/api/gate/status`)
            .then((r) => r.json())
            .then((d) => setState(d.enabled ? 'locked' : 'open'))
            // Trade-off: if the status call fails on a prerendered page, keep showing the
            // content — it is already public in the raw HTML, so the gate protects nothing
            // on these routes. Only non-prerendered loads fail closed.
            .catch(() => setState(optimistic ? 'open' : 'locked'));
    }, [state]);

    if (state === 'open' || state === 'open-unconfirmed') return children;
    if (state === 'checking') return <div className="fixed inset-0 bg-black" data-testid="site-gate-loading" />;

    const submit = async (e) => {
        e.preventDefault();
        if (!code.trim() || busy) return;
        setBusy(true);
        setError('');
        try {
            const r = await fetch(`${API}/api/gate/unlock`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: code.trim() }),
            });
            if (r.ok) {
                try { localStorage.setItem(KEY, '1'); } catch { /* ignore */ }
                setState('open');
            } else {
                setError('ACCESS DENIED — incorrect code');
            }
        } catch {
            setError('Connection error — try again');
        }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black px-6" data-testid="site-gate">
            <div className="w-full max-w-sm text-center font-mono">
                <div className="mb-2 text-[11px] tracking-[0.35em] text-emerald-500/70">SYNTHETIC MEDIA INVESTIGATOR</div>
                <div className="mb-8 text-xs tracking-[0.25em] text-zinc-500">RESTRICTED FACILITY · ACCESS CODE REQUIRED</div>
                <form onSubmit={submit} noValidate>
                    <input
                        type="password"
                        inputMode="numeric"
                        autoFocus
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder="ENTER ACCESS CODE"
                        data-testid="site-gate-input"
                        className="w-full rounded-none border border-zinc-700 bg-zinc-950 px-4 py-3 text-center text-lg tracking-[0.5em] text-emerald-400 placeholder:text-zinc-700 placeholder:tracking-[0.2em] placeholder:text-xs focus:border-emerald-500 focus:outline-none"
                    />
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="site-gate-submit"
                        className="mt-4 w-full border border-emerald-600/60 bg-emerald-950/40 px-4 py-3 text-xs tracking-[0.35em] text-emerald-400 transition-colors hover:bg-emerald-900/40 disabled:opacity-50"
                    >
                        {busy ? 'VERIFYING…' : 'UNLOCK'}
                    </button>
                </form>
                {error && (
                    <div className="mt-4 text-xs tracking-widest text-red-500" data-testid="site-gate-error">{error}</div>
                )}
            </div>
        </div>
    );
};
